import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getMyP2PProfile, getMyOrders } from '../services/p2pService';

interface P2PContextType {
  socket: Socket | null;
  profile: any;
  activeOrders: any[];
  unreadCounts: Record<string, number>;
  refreshProfile: () => void;
  refreshOrders: () => void;
  markRead: (orderId: string) => void;
}

const P2PContext = createContext<P2PContextType | undefined>(undefined);

export function P2PProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [activeOrders, setActiveOrders] = useState<any[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getMyP2PProfile();
      setProfile(data);
    } catch { /* ignore */ }
  }, [user]);

  const refreshOrders = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getMyOrders({ status: 'pending_payment' });
      const pending = data?.orders ?? [];
      const sent = await getMyOrders({ status: 'payment_sent' });
      setActiveOrders([...pending, ...(sent?.orders ?? [])]);
    } catch { /* ignore */ }
  }, [user]);

  const markRead = useCallback((orderId: string) => {
    setUnreadCounts(prev => ({ ...prev, [orderId]: 0 }));
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshProfile();
    refreshOrders();

    const token = (() => {
      try { return JSON.parse(localStorage.getItem('userInfo') || '{}').token; } catch { return null; }
    })();
    if (!token) return;

    const socket = io(`${import.meta.env.VITE_P2P_SOCKET_URL}/p2p`, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('order:new', ({ order }: { order: any }) => {
      setActiveOrders(prev => {
        const exists = prev.some(o => o._id === order._id);
        return exists ? prev : [order, ...prev];
      });
    });

    socket.on('order:message', ({ orderId }: { orderId: string }) => {
      setUnreadCounts(prev => ({ ...prev, [orderId]: (prev[orderId] ?? 0) + 1 }));
    });

    socket.on('order:status', () => { refreshOrders(); });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user]);

  return (
    <P2PContext.Provider value={{ socket: socketRef.current, profile, activeOrders, unreadCounts, refreshProfile, refreshOrders, markRead }}>
      {children}
    </P2PContext.Provider>
  );
}

export function useP2P() {
  const ctx = useContext(P2PContext);
  if (!ctx) throw new Error('useP2P must be used within P2PProvider');
  return ctx;
}
